// Orchestrates the creepypastas: when they appear, what they say (via the LLM
// server), and — crucially — the deterministic rules that kill you. The dialogue
// is pure persuasion/flavour; the lethal act is always something the player
// physically does (opens the door / turns to look). That split is the whole game.

import THREE from "../three.js";
import type { PerspectiveCamera } from "three";
import type { Hud } from "../hud.js";
import type { Controller } from "../controls.js";
import type { Routine } from "../clock.js";
import type { World } from "../world/layout.js";
import type {
  DialogueResponse,
  DialogueTurn,
  DialogueContext,
} from "../../shared/types.js";
import { buildFace, buildBurntMan } from "./avatars.js";

type Kind = "window" | "burnt" | "drain";
/** The perturbing sensory effect each kind imposes once it's truly bearing down
 *  on you (high dread). Mirrors the server's per-archetype EFFECT map. */
export type PlayEffect = "invert" | "hallucinate" | "voices";

interface Active {
  id: string;
  kind: Kind;
  remove: () => void;
  anim: (t: number, dread: number) => void;
  /** world position the player must NOT centre on (burnt man only) */
  lethalLookAt?: THREE.Vector3;
  history: DialogueTurn[];
  refusals: number;
  age: number;
  busy: boolean;
  ended: boolean;
}

export interface GameHooks {
  onDeath: (reason: string) => void;
  onReward: () => void;
}

const LOOK_ANGLE = (18 * Math.PI) / 180; // within this of straight-on = "looking"
const LOOK_GRACE = 0.18; // seconds you can graze it before it counts

export class EntityManager {
  dread = 0;
  private active: Active | null = null;
  private spawnTimer = 0;
  private lookTimer = 0;
  /** Don't re-summon the same drain in the same cool phase. */
  private drainPhaseUsed = "";

  constructor(
    private scene: THREE.Scene,
    private camera: PerspectiveCamera,
    private world: World,
    private controller: Controller,
    private hud: Hud,
    private routine: Routine,
    private hooks: GameHooks,
  ) {}

  reset() {
    if (this.active) this.active.remove();
    this.active = null;
    this.spawnTimer = 0;
    this.lookTimer = 0;
    this.dread = 0;
    this.drainPhaseUsed = "";
    this.hud.hideDialogue();
    this.hud.setDread(0);
  }

  update(dt: number, time: number) {
    const pos = this.controller.position();
    const room = this.world.roomAt(pos.x, pos.z);
    const seated = this.controller.isSeated;
    const phase = this.routine.phase;

    if (this.active) {
      this.active.age += dt;
      this.active.anim(time, this.dread);

      // Ambient dread climbs slowly while an entity is present.
      this.dread = Math.min(1, this.dread + dt * 0.03);
      this.hud.setDread(this.dread);

      // Lethal-look rule (burnt man).
      if (this.active.lethalLookAt && !this.active.ended) {
        if (this.isLookingAt(this.active.lethalLookAt)) {
          this.lookTimer += dt;
          if (this.lookTimer >= LOOK_GRACE) {
            this.kill("You looked. It only ever wanted to be looked at.");
            return;
          }
        } else {
          this.lookTimer = 0;
        }
      }

      // Despawn only when the player leaves the ROOM — standing up to walk to
      // the door must NOT banish the face, or the open-the-door trap is toothless.
      const stillValid = this.contextFor(this.active.kind, room);
      if (!stillValid && this.active.age > 1.5) this.leaveActive();
      return;
    }

    // Nothing active → decay dread, look for a trigger.
    this.dread = Math.max(0, this.dread - dt * 0.08);
    this.hud.setDread(this.dread);

    const want = this.wantedKind(room, seated, phase);
    if (!want) {
      this.spawnTimer = 0;
      return;
    }
    this.spawnTimer += dt;
    const delay = want === "burnt" ? 4.5 : want === "window" ? 6 : 7;
    if (this.spawnTimer >= delay) {
      this.spawnTimer = 0;
      void this.spawn(want);
    }
  }

  /** The perturbing effect to impose this frame, or null. Only bites once a
   *  presence is here AND dread has risen — the room turning against you as it
   *  bears down. window → inverted look; burnt → heat hallucination; drain →
   *  whispering. */
  currentEffect(): PlayEffect | null {
    if (!this.active || this.active.ended) return null;
    if (this.dread < 0.4) return null;
    const k = this.active.kind;
    return k === "window" ? "invert" : k === "burnt" ? "hallucinate" : "voices";
  }

  // ── External rule hooks (called from main.ts) ──
  /** The player opened a door. If the face is begging at the window, that's fatal. */
  notifyDoorOpened(doorName: string) {
    if (this.active?.kind === "window" && doorName === "dry") {
      this.kill("You opened the door. Whatever was at the glass is inside now.");
    }
  }
  /** The player turned the cold shower on. Satisfies the drain. */
  notifyShowerOn() {
    if (this.active?.kind === "drain") {
      this.reward();
    }
  }

  // ── Spawning ──
  private wantedKind(room: string, seated: boolean, phase: string): Kind | null {
    if (room === "dry_sauna" && seated && phase === "dry") return "window";
    if (room === "steam_sauna" && seated && phase === "steam") return "burnt";
    if (room === "rest" && (phase === "cool1" || phase === "cool2")) {
      if (this.drainPhaseUsed === phase) return null;
      return "drain";
    }
    return null;
  }
  /** Persistence rule: the entity stays as long as you're in its room. */
  private contextFor(kind: Kind, room: string): boolean {
    if (kind === "window") return room === "dry_sauna";
    if (kind === "burnt") return room === "steam_sauna";
    return room === "rest";
  }

  private async spawn(kind: Kind) {
    const id = kind === "window" ? "window_guy" : kind === "burnt" ? "burnt_man" : "the_drain";
    let active: Active;

    if (kind === "window") {
      const face = buildFace();
      const a = this.world.windowAnchor;
      face.group.position.copy(a.position);
      face.group.lookAt(a.position.x, a.position.y, a.position.z + 1); // face the interior
      this.scene.add(face.group);
      active = {
        id,
        kind,
        remove: () => this.scene.remove(face.group),
        anim: (_t, dread) => face.press(dread),
        history: [],
        refusals: 0,
        age: 0,
        busy: false,
        ended: false,
      };
    } else if (kind === "burnt") {
      const bm = buildBurntMan();
      const seat = this.controller.position();
      // Sit it on the bench beside the player, staying inside the SAME sauna
      // (the divider is at x=5; benches sit at z≈3.5 against the south wall).
      const inDry = seat.x < 5;
      const bx = inDry ? Math.min(seat.x + 0.95, 4.5) : Math.max(seat.x - 0.95, 5.5);
      bm.group.position.set(bx, 0, 3.5);
      this.scene.add(bm.group);
      const headWorld = new THREE.Vector3(bm.group.position.x, 1.4, bm.group.position.z);
      active = {
        id,
        kind,
        remove: () => this.scene.remove(bm.group),
        anim: (t) => bm.seethe(t),
        lethalLookAt: headWorld,
        history: [],
        refusals: 0,
        age: 0,
        busy: false,
        ended: false,
      };
    } else {
      // drain — no body, just a voice. Mark this phase used so it appears once.
      this.drainPhaseUsed = this.routine.phase;
      active = {
        id,
        kind,
        remove: () => {},
        anim: () => {},
        history: [],
        refusals: 0,
        age: 0,
        busy: false,
        ended: false,
      };
    }

    this.active = active;
    const opening = await this.requestSay(active, undefined);
    if (this.active === active) this.applyResponse(active, opening);
  }

  // ── Dialogue ──
  private async requestSay(active: Active, playerAction?: string): Promise<DialogueResponse> {
    active.busy = true;
    const context: DialogueContext = {
      room: (this.world.roomAt(this.controller.position().x, this.controller.position().z) as
        | "reception"
        | "rest"
        | "dry_sauna"
        | "steam_sauna"),
      clock: this.routine.clockString(),
      minutesInRoom: Math.floor(
        active.kind === "window" ? this.routine.dry : active.kind === "burnt" ? this.routine.steam : this.routine.cool,
      ),
      refusals: active.refusals,
    };
    try {
      const res = await fetch("/api/entity-say", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entityId: active.id, history: active.history, playerAction, context }),
      });
      const data = (await res.json()) as DialogueResponse;
      active.busy = false;
      return data;
    } catch {
      active.busy = false;
      return {
        entityId: active.id,
        say: "...",
        action: "none",
        choices: [{ id: "wait", label: "Wait" }],
        dread: this.dread,
      };
    }
  }

  private applyResponse(active: Active, res: DialogueResponse) {
    active.history.push({ role: "entity", text: res.say });
    this.dread = Math.max(this.dread, res.dread);

    if (res.action === "attack") {
      this.hud.showDialogue(res.say, [], () => {});
      setTimeout(() => this.kill(this.deathLineFor(active.kind)), 650);
      active.ended = true;
      return;
    }
    if (res.action === "leave") {
      this.hud.showDialogue(res.say, [], () => {});
      setTimeout(() => this.leaveActive(), 1400);
      active.ended = true;
      return;
    }
    if (res.action === "reward") {
      this.hud.showDialogue(res.say, [], () => {});
      setTimeout(() => this.reward(), 1400);
      active.ended = true;
      return;
    }
    if (res.action === "escalate") this.dread = Math.min(1, this.dread + 0.15);

    this.hud.showDialogue(res.say, res.choices, (id) => this.onChoose(active, id));
  }

  private async onChoose(active: Active, choiceId: string) {
    if (active.busy || active.ended || this.active !== active) return;
    const label = this.lastChoiceLabel(choiceId);
    active.history.push({ role: "player", text: label });

    // The trap: if the player verbally COMPLIES with the entity's one demand,
    // that's as fatal as doing it physically. The live LLM often dangles a
    // "fine, I'll let you in" / "okay, I'll look" option — those must kill.
    if (this.lethalChoice(active.kind, choiceId)) {
      this.kill(this.deathLineFor(active.kind));
      return;
    }
    if (choiceId === "refuse" || choiceId === "ignore" || choiceId === "eyes_forward") {
      active.refusals++;
    }
    this.hud.showDialogue("…", [], () => {}); // thinking beat
    const res = await this.requestSay(active, choiceId);
    if (this.active === active) this.applyResponse(active, res);
  }

  /** Does this reply id amount to giving the entity what it fatally wants? */
  private lethalChoice(kind: Kind, id: string): boolean {
    const s = id.toLowerCase();
    if (kind === "window") return /(agree|open|let.?(him|it|you)?.?in|unlock|invite|comply|^yes)/.test(s);
    if (kind === "burnt") return /(look|turn|glance|peek|face|^see)/.test(s);
    return false; // the drain wants compliance — that's the SAFE path, not lethal
  }

  private lastChoiceLabel(id: string): string {
    // We don't keep the button list around; the id is descriptive enough for the
    // model, and the player already saw the label. Send a readable token.
    return id.replace(/_/g, " ");
  }

  // ── Outcomes ──
  private kill(reason: string) {
    if (!this.active) return;
    this.hud.redFlash();
    this.hud.hideDialogue();
    this.active.remove();
    this.active = null;
    this.hooks.onDeath(reason);
  }
  private reward() {
    this.hud.hideDialogue();
    this.hud.toastMsg("The dread eases. For now.");
    this.dread = Math.max(0, this.dread - 0.5);
    if (this.active) {
      this.active.remove();
      this.active = null;
    }
    this.hooks.onReward();
  }
  private leaveActive() {
    if (!this.active) return;
    this.hud.hideDialogue();
    this.active.remove();
    this.active = null;
    this.lookTimer = 0;
  }

  private deathLineFor(kind: Kind): string {
    if (kind === "window") return "You opened the door. Whatever was at the glass is inside now.";
    if (kind === "burnt") return "You looked. It only ever wanted to be looked at.";
    return "It took what it wanted.";
  }

  private isLookingAt(target: THREE.Vector3): boolean {
    const cam = this.camera.position;
    const fwd = this.controller.forward(); // horizontal
    const to = new THREE.Vector3(target.x - cam.x, 0, target.z - cam.z);
    if (to.lengthSq() < 1e-4) return false;
    to.normalize();
    const dot = fwd.x * to.x + fwd.z * to.z;
    return Math.acos(Math.max(-1, Math.min(1, dot))) < LOOK_ANGLE;
  }
}
